/**
 * Version Branch System
 *
 * Allows creating alternate versions of drafts from any point in time.
 * Think of it like Git branches for your drafts.
 */

export class DraftVersion {
  constructor(data = {}) {
    this.versionId = data.versionId || this.generateVersionId();
    this.draftId = data.draftId;
    this.parentVersionId = data.parentVersionId || null; // null for root version
    this.branchName = data.branchName || 'main';
    this.timestamp = data.timestamp || Date.now();
    this.userId = data.userId;
    this.userName = data.userName;
    this.message = data.message || '';
    this.snapshot = data.snapshot; // Full draft state at this version
    this.changes = data.changes || []; // Array of changes from parent
    this.isActive = data.isActive !== undefined ? data.isActive : true;
  }

  generateVersionId() {
    return `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  toJSON() {
    return {
      versionId: this.versionId,
      draftId: this.draftId,
      parentVersionId: this.parentVersionId,
      branchName: this.branchName,
      timestamp: this.timestamp,
      userId: this.userId,
      userName: this.userName,
      message: this.message,
      snapshot: this.snapshot,
      changes: this.changes,
      isActive: this.isActive,
    };
  }

  static fromJSON(data) {
    return new DraftVersion(data);
  }
}

export class VersionBranchService {
  /**
   * Create a new version snapshot
   */
  static createVersion(draftId, draftSnapshot, userId, userName, message = '', parentVersionId = null, branchName = 'main') {
    const version = new DraftVersion({
      draftId,
      parentVersionId,
      branchName,
      userId,
      userName,
      message,
      snapshot: JSON.parse(JSON.stringify(draftSnapshot)), // Deep clone
    });

    return version;
  }

  /**
   * Create a new branch from a specific version
   */
  static createBranch(baseVersion, branchName, userId, userName) {
    const newVersion = new DraftVersion({
      draftId: baseVersion.draftId,
      parentVersionId: baseVersion.versionId,
      branchName,
      userId,
      userName,
      message: `Created branch: ${branchName}`,
      snapshot: JSON.parse(JSON.stringify(baseVersion.snapshot)),
    });

    return newVersion;
  }

  /**
   * Get version tree structure for visualization
   */
  static buildVersionTree(versions) {
    const versionMap = new Map();
    const roots = [];

    // Build map
    versions.forEach(version => {
      versionMap.set(version.versionId, {
        ...version,
        children: [],
      });
    });

    // Build tree
    versions.forEach(version => {
      const node = versionMap.get(version.versionId);
      if (version.parentVersionId) {
        const parent = versionMap.get(version.parentVersionId);
        if (parent) {
          parent.children.push(node);
        } else {
          roots.push(node);
        }
      } else {
        roots.push(node);
      }
    });

    return roots;
  }

  /**
   * Get all branches for a draft
   */
  static getBranches(versions) {
    const branches = new Map();

    versions.forEach(version => {
      if (!branches.has(version.branchName)) {
        branches.set(version.branchName, []);
      }
      branches.get(version.branchName).push(version);
    });

    // Sort each branch by timestamp
    branches.forEach((versionList, branchName) => {
      versionList.sort((a, b) => a.timestamp - b.timestamp);
    });

    return Object.fromEntries(branches);
  }

  /**
   * Calculate changes between two versions
   */
  static calculateDiff(versionA, versionB) {
    const changes = [];
    const snapshotA = versionA.snapshot;
    const snapshotB = versionB.snapshot;

    // Compare scenarios
    const scenariosA = snapshotA.scenariosFirstPick || [];
    const scenariosB = snapshotB.scenariosFirstPick || [];

    scenariosA.forEach((scenarioA, index) => {
      const scenarioB = scenariosB[index];
      if (!scenarioB) {
        changes.push({
          type: 'scenario_removed',
          scenarioId: scenarioA.id,
          index,
        });
        return;
      }

      // Compare picks
      scenarioA.picks.forEach((pickA, pickIndex) => {
        const pickB = scenarioB.picks[pickIndex];
        if (pickA !== pickB) {
          changes.push({
            type: 'pick_changed',
            scenarioId: scenarioA.id,
            position: pickIndex,
            oldValue: pickA,
            newValue: pickB,
          });
        }
      });
    });

    // Check for new scenarios
    if (scenariosB.length > scenariosA.length) {
      for (let i = scenariosA.length; i < scenariosB.length; i++) {
        changes.push({
          type: 'scenario_added',
          scenarioId: scenariosB[i].id,
          index: i,
        });
      }
    }

    return changes;
  }

  /**
   * Merge two branches (simple version - take all changes from source branch)
   */
  static mergeBranches(targetVersion, sourceVersion, userId, userName) {
    const mergedSnapshot = JSON.parse(JSON.stringify(sourceVersion.snapshot));

    const mergeVersion = new DraftVersion({
      draftId: targetVersion.draftId,
      parentVersionId: targetVersion.versionId,
      branchName: targetVersion.branchName,
      userId,
      userName,
      message: `Merged ${sourceVersion.branchName} into ${targetVersion.branchName}`,
      snapshot: mergedSnapshot,
    });

    return mergeVersion;
  }

  /**
   * Get version history path from root to specific version
   */
  static getVersionPath(versions, targetVersionId) {
    const versionMap = new Map(versions.map(v => [v.versionId, v]));
    const path = [];
    let currentVersion = versionMap.get(targetVersionId);

    while (currentVersion) {
      path.unshift(currentVersion);
      currentVersion = currentVersion.parentVersionId
        ? versionMap.get(currentVersion.parentVersionId)
        : null;
    }

    return path;
  }
}

export default VersionBranchService;
